import { DOCUMENT, Location } from '@angular/common';
import type {
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import type {
  AccountInfo,
  AuthenticationResult,
  IPublicClientApplication,
  PopupRequest,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { StringUtils } from '@azure/msal-browser';
import { InteractionType } from '@azure/msal-browser';
import type { Observable } from 'rxjs';
import { EMPTY, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { ConfigurationService } from '#/app/shared/services/configuration.service';

import { IdentityProvider } from './identity-provider';

type MsalInterceptorAuthRequest =
  | Omit<PopupRequest, 'scopes'>
  | Omit<RedirectRequest, 'scopes'>
  | Omit<SilentRequest, 'scopes'>;

type ProtectedResourceMap = Map<
  string,
  (string | ProtectedResourceScopes)[] | null
>;

type MsalInterceptorAuthRequestFn = (
  msalService: IPublicClientApplication,
  req: HttpRequest<unknown>,
  originalAuthRequest: MsalInterceptorAuthRequest,
) => MsalInterceptorAuthRequest;

interface MsalInterceptorConfiguration {
  interactionType: InteractionType.Popup | InteractionType.Redirect;
  protectedResourceMap: ProtectedResourceMap;
  authRequest?: MsalInterceptorAuthRequest | MsalInterceptorAuthRequestFn;
}

export interface ProtectedResourceScopes {
  httpMethod: string;
  scopes: string[] | null;
}

/**
 * Factory function for MSAL HTTP interceptor.
 * @returns An `HttpInterceptor` that will attach an authorization token to outgoing requests.
 */
export function msalInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const document = inject(DOCUMENT);
  const location = inject(Location);
  const identityProvider = inject(IdentityProvider);
  const configurationService = inject(ConfigurationService);

  const msalInterceptorConfig: MsalInterceptorConfiguration = {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      ['/api/public/*', null],
      ['/api/*', configurationService.authConfig.entraId.scopes],
      ['https://graph.microsoft.com/v1.0/me', ['user.read']],
    ]),
    authRequest: (_msalService, _httpReq, originalAuthRequest) =>
      originalAuthRequest,
  };

  function getAbsoluteUrl(url: string): string {
    const link = document.createElement('a');
    link.href = url;
    return link.href;
  }

  function checkUrlComponents(
    keyComponents: URL,
    endpointComponents: URL,
  ): boolean {
    const urlProperties = [
      'protocol',
      'host',
      'pathname',
      'search',
      'hash',
    ] as const;
    for (const property of urlProperties) {
      if (keyComponents[property]) {
        const decodedInput = decodeURIComponent(keyComponents[property]);
        if (
          !StringUtils.matchPattern(decodedInput, endpointComponents[property])
        ) {
          return false;
        }
      }
    }
    return true;
  }

  function matchResourcesToEndpoint(
    protectedResourcesEndpoints: string[],
    endpoint: string,
  ): string[] {
    const matchingResources: string[] = [];
    for (const key of protectedResourcesEndpoints) {
      const normalizedKey = location.normalize(key);
      const absoluteKey = getAbsoluteUrl(normalizedKey);
      const keyComponents = new URL(absoluteKey);
      const absoluteEndpoint = getAbsoluteUrl(endpoint);
      const endpointComponents = new URL(absoluteEndpoint);
      if (checkUrlComponents(keyComponents, endpointComponents)) {
        matchingResources.push(key);
      }
    }
    return matchingResources;
  }

  function matchScopesToEndpoint(
    protectedResourceMap: Map<
      string,
      (string | ProtectedResourceScopes)[] | null
    >,
    endpointArray: string[],
    httpMethod: string,
  ): string[] | null {
    const allMatchedScopes: (string[] | null)[] = [];
    for (const matchedEndpoint of endpointArray) {
      const scopesForEndpoint: string[] | null = [];
      const methodAndScopesArray = protectedResourceMap.get(matchedEndpoint);
      if (!methodAndScopesArray) {
        allMatchedScopes.push(null);
        continue;
      }
      for (const entry of methodAndScopesArray) {
        if (typeof entry === 'string') {
          scopesForEndpoint.push(entry);
        } else {
          const normalizedRequestMethod = httpMethod.toLowerCase();
          const normalizedResourceMethod = entry.httpMethod.toLowerCase();
          if (normalizedResourceMethod === normalizedRequestMethod) {
            if (entry.scopes === null) {
              allMatchedScopes.push(null);
            } else {
              for (const scope of entry.scopes) {
                scopesForEndpoint.push(scope);
              }
            }
          }
        }
      }
      if (scopesForEndpoint.length > 0) {
        allMatchedScopes.push(scopesForEndpoint);
      }
    }
    if (allMatchedScopes.length > 0) {
      return allMatchedScopes[0];
    }
    return null;
  }

  function getScopesForEndpoint(
    endpoint: string,
    httpMethod: string,
  ): string[] | null {
    const normalizedEndpoint = location.normalize(endpoint);
    const protectedResourcesArray = Array.from(
      msalInterceptorConfig.protectedResourceMap.keys(),
    );
    const matchingProtectedResources = matchResourcesToEndpoint(
      protectedResourcesArray,
      normalizedEndpoint,
    );
    if (matchingProtectedResources.length > 0) {
      return matchScopesToEndpoint(
        msalInterceptorConfig.protectedResourceMap,
        matchingProtectedResources,
        httpMethod,
      );
    }
    return null;
  }

  function acquireTokenInteractively(
    authRequest: MsalInterceptorAuthRequest,
    scopes: string[],
    currentMsalInstance: IPublicClientApplication,
  ): Observable<AuthenticationResult> {
    if (msalInterceptorConfig.interactionType === InteractionType.Popup) {
      return from(
        currentMsalInstance.acquireTokenPopup({ ...authRequest, scopes }),
      );
    }
    const redirectStartPage = window.location.href;
    void currentMsalInstance.acquireTokenRedirect({
      ...authRequest,
      scopes,
      redirectStartPage,
    });
    return EMPTY;
  }

  function acquireToken(
    authRequest: MsalInterceptorAuthRequest,
    scopes: string[],
    account: AccountInfo,
    currentMsalInstance: IPublicClientApplication,
  ): Observable<AuthenticationResult> {
    return from(
      currentMsalInstance.acquireTokenSilent({
        ...authRequest,
        scopes,
        account,
      }),
    ).pipe(
      catchError(() =>
        acquireTokenInteractively(authRequest, scopes, currentMsalInstance),
      ),
      switchMap((result: AuthenticationResult) => {
        if (!result.accessToken) {
          return acquireTokenInteractively(
            authRequest,
            scopes,
            currentMsalInstance,
          );
        }
        return of(result);
      }),
    );
  }

  const currentMsalInstance = identityProvider.instance.value();
  const scopes = getScopesForEndpoint(req.url, req.method);
  if (!currentMsalInstance || !scopes || scopes.length === 0) {
    return next(req);
  }
  const account =
    currentMsalInstance.getActiveAccount() ??
    currentMsalInstance.getAllAccounts()[0];
  const authRequest =
    typeof msalInterceptorConfig.authRequest === 'function'
      ? msalInterceptorConfig.authRequest(currentMsalInstance, req, {
          account,
        })
      : { ...msalInterceptorConfig.authRequest, account };
  return from(
    acquireToken(authRequest, scopes, account, currentMsalInstance),
  ).pipe(
    switchMap((result: AuthenticationResult) => {
      const headers = req.headers.set(
        'Authorization',
        `Bearer ${result.accessToken}`,
      );
      const requestClone = req.clone({ headers });
      return next(requestClone);
    }),
  );
}
